import Conviva from '@convivainc/conviva-js-coresdk';
import { ClientType, ColorSpace } from '@mlbtv-clients/services';
import { WIRED, WIRELESS } from 'analytics/conviva/constants';
import { EASTERN_TZ } from 'constants/date';
import { MODEL_YEAR_UNKNOWN } from 'constants/platforms';
import { ModelTier } from 'hooks/useDeviceModelTier/types';
import { getModelTier } from 'hooks/useDeviceModelTier/utils';
import { EPGErrors } from 'services/epg/constants';
import fallback from 'store/config/fallback.json';
import { DeviceCaptionStyle } from '../@types/window';
import {
  CaptionColor,
  CaptionEdge,
  CaptionFontSize,
  CaptionFontStyle,
  CaptionOpacity,
  defaultCCStyles,
} from './platformTypes';

const { EPG_SERVICE_ERROR } = EPGErrors;

export type DeviceInfo = {
  brandName: string;
  deviceModel: string;
  manufacturer: string;
  operatingSystemName: string;
  operatingSystemVersion: string;
  operatingSystems: string;
};

interface DeviceMetadata {
  deviceBrand: string;
  deviceManufacturer: string;
  deviceModel: string;
  operatingSystemName: string;
}

export type WebviewMessageValue = string | boolean | DeviceInfo | DeviceCaptionStyle;

export interface WebviewMessage<T extends WebviewMessageValue> {
  args: T;
  method: string;
}

const {
  Constants: { DeviceCategory, DeviceMetadata, DeviceType },
} = Conviva;

const XBOX_MODELS = [
  'Xbox Series X',
  'Xbox Series S',
  'Xbox One X',
  'Xbox One S',
  'Xbox One',
] as const;

const parseModelFromUserAgent = (): string | undefined => {
  const ua = navigator.userAgent;
  const lower = ua.toLowerCase();
  return XBOX_MODELS.find((model) => lower.includes(model.toLowerCase()));
};

const {
  BRAND,
  CATEGORY,
  MANUFACTURER,
  MODEL,
  OS_NAME,
  OS_VERSION,
  SCREEN_RESOLUTION_HEIGHT,
  SCREEN_RESOLUTION_WIDTH,
  TYPE,
} = DeviceMetadata;

export const getClientType = async () => ClientType.XBOX;

export const getDeviceOSVersion = async (defaultOS: string) => {
  const info = await messageWebview('GetDeviceMetadata', {} as DeviceInfo);
  return info?.operatingSystemVersion || defaultOS;
};

export const getDeviceAdParams = (adParams: Record<string, string>, ifaParams: unknown) => adParams;

export const getDeviceCaptionStyle = async () => {
  const result = await messageWebview<DeviceCaptionStyle>(
    'GetDeviceClosedCaptionData',
    defaultCCStyles,
  );

  const {
    ccBackgroundColor,
    ccBackgroundOpacity,
    ccFontColor,
    ccFontEffect,
    ccFontOpacity,
    ccFontSize,
    ccFontStyle,
    ccRegionColor,
  } = result;

  return `
    video::cue {
      background-color: ${
        ccBackgroundColor === 'CAPTION_COLOR_DEFAULT'
          ? `rgba(${CaptionColor['CAPTION_COLOR_BLACK']},${CaptionOpacity[ccFontOpacity]})`
          : `rgba(${CaptionColor[ccBackgroundColor]},${CaptionOpacity[ccFontOpacity]})`
      };
      border: 2rem solid rgba(${CaptionColor[ccRegionColor]},${CaptionOpacity[ccBackgroundOpacity]});
      color: rgba(${CaptionColor[ccFontColor]},${CaptionOpacity[ccFontOpacity]});
      font-family: ${CaptionFontStyle[ccFontStyle]};
      font-size: ${CaptionFontSize[ccFontSize]};
      line-height: 1.15;
      text-shadow: ${CaptionEdge[ccFontEffect]?.replaceAll('color', CaptionColor[ccFontColor])};
    }

    video::-webkit-media-text-track-display {
      background-color: ${
        ccBackgroundColor === 'CAPTION_COLOR_DEFAULT'
          ? `rgba(${CaptionColor['CAPTION_COLOR_BLACK']},${CaptionOpacity[ccFontOpacity]})`
          : `rgba(${CaptionColor[ccBackgroundColor]},${CaptionOpacity[ccBackgroundOpacity]})`
      };
      border: 2rem solid rgba(${CaptionColor[ccRegionColor]},${CaptionOpacity[ccBackgroundOpacity]});
      font-variant: ${ccFontStyle === 'CAPTION_FONT_STYLE7' ? 'small-caps' : ''};
      line-height: 1;
    }
  `;
};

export const getDeviceCountryCode = async () => {
  const result = messageWebview('GetDeviceCountryCode', '');
  return result;
};

export const getDeviceConnectionType = async () => {
  const isWireless = await messageWebview('GetDeviceConnectionType', true);
  return isWireless ? WIRELESS : WIRED;
};

export const getDeviceExitApp = () => {
  // no exit app
};

export const getDeviceExitConfirmation = () => {
  // no exit dialog
};

export const getDeviceId = async (deviceId: string) => {
  const result = await messageWebview('GetDeviceId', deviceId);
  return result;
};

export const getDeviceIdType = () => 'dpid';

export const getDeviceKeyboardIsOpen = async () => {
  const result = await messageWebview('GetDeviceKeyboardIsOpen', false);
  return result;
};

export const getDeviceMetadata = async (deviceMetadata: DeviceMetadata) => {
  const { deviceBrand, deviceManufacturer, deviceModel, operatingSystemName } = deviceMetadata;

  const info = await messageWebview('GetDeviceMetadata', {} as DeviceInfo);
  return {
    [BRAND]: info?.brandName ?? deviceBrand,
    [CATEGORY]: DeviceCategory.XBOX,
    [MANUFACTURER]: deviceManufacturer,
    [MODEL]: parseModelFromUserAgent() ?? deviceModel,
    [OS_NAME]: info?.operatingSystemName ?? operatingSystemName,
    [OS_VERSION]: info?.operatingSystemVersion,
    [SCREEN_RESOLUTION_HEIGHT]: window.screen.height,
    [SCREEN_RESOLUTION_WIDTH]: window.screen.width,
    [TYPE]: DeviceType.CONSOLE,
  };
};

export const getDeviceModel = async (defaultValue: string) =>
  parseModelFromUserAgent() ?? defaultValue;

export const getDeviceModelYear = async () => MODEL_YEAR_UNKNOWN;

// TODO: [COW-773]
export const getDevicePlaybackCapabilities = async () => ({ colorSpace: [ColorSpace.SDR] });

export const getDeviceHasSpinner = async (
  hasSpinner: typeof fallback.featureFlags.animations.hasSpinner,
) => !hasSpinner.hasAllModelsDisabled;

async function messageWebview<T extends WebviewMessageValue>(
  message: string,
  fallbackMessage: T,
): Promise<T> {
  const callback = (webviewMessage: WebviewMessage<T>): T =>
    webviewMessage.method === message ? webviewMessage.args : fallbackMessage;

  const apiCall = new Promise<T>((resolve) => {
    const { webview } = window.chrome;
    if (webview === undefined) throw new Error('Webview container not found.');

    const resolveCallback = (arg: { data: WebviewMessage<T> }) => resolve(callback(arg.data));
    // use once: true to remove the event listener after the first event is fired.
    webview.addEventListener('message', resolveCallback, { once: true });
    webview.postMessage(message);
  }).catch((e) => {
    throw e;
  });

  try {
    return await apiCall;
  } catch (e) {
    console.error(e);
    return fallbackMessage;
  }
}

export const getDeviceModelTier = async (modelTiers: Record<string, ModelTier>) =>
  getModelTier(modelTiers, '');

export const getDeviceSKU = async (defaultValue: string) => defaultValue;

export const getDeviceTimeZone = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!timeZone) {
    throw new Error(`${EPG_SERVICE_ERROR} Time zone not found. Falling back to ${EASTERN_TZ}`);
  }

  return timeZone;
};

export const getDeviceTTSEnabled = () => false;
